import type { ArtifactKind, Artifacts } from "@procurement-agents/scope-api";
import { useCallback, useEffect, useState } from "react";

export const ARTIFACTS: Array<{ id: ArtifactKind; label: string }> = [
  { id: "requisition", label: "Requisition" }, { id: "market_intel", label: "Market intelligence" }, { id: "structure", label: "Structure decision" },
  { id: "plan", label: "Section plan" }, { id: "sow", label: "Draft SoW" }, { id: "qa", label: "QA report" },
];

/** Per-user "seen" timestamps in local UI state so "new" / "updated" badges clear when opened. */
export function useSeen(runId: string) {
  const key = `workspace.seen.${runId}`;
  const [seen, setSeen] = useState<Record<string, string>>(() => { try { return JSON.parse(localStorage.getItem(key) ?? "{}"); } catch { return {}; } });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(seen)); } catch { /* ignore */ } }, [key, seen]);
  const mark = useCallback((id: string, at: string | null) => setSeen((s) => ({ ...s, [id]: at ?? new Date().toISOString() })), []);
  return { seen, mark };
}

/** KRE-129: the rail lists the artifacts a view provides, and only those: a requester's view leaves out procurement's
 *  materials altogether rather than showing them greyed. `labels` renames one for the reader ("Final scope"). */
export function ArtifactsRail({ artifacts, selected, onSelect, seen, children, labels }: { artifacts: Partial<Artifacts>; selected: ArtifactKind | null; onSelect: (id: ArtifactKind | null) => void; seen: Record<string, string>; children?: React.ReactNode; labels?: Partial<Record<ArtifactKind, string>> }) {
  const listed = ARTIFACTS.filter((a) => a.id in artifacts);
  const onKey = (e: React.KeyboardEvent<HTMLUListElement>) => {
    const ids = listed.filter((a) => artifacts[a.id]?.exists).map((a) => a.id);
    const i = selected ? ids.indexOf(selected) : -1;
    if (e.key === "ArrowDown") { e.preventDefault(); onSelect(ids[Math.min(ids.length - 1, i + 1)] ?? null); }
    if (e.key === "ArrowUp") { e.preventDefault(); onSelect(ids[Math.max(0, i - 1)] ?? null); }
  };
  return (
    <aside className="artifacts" aria-label="Artifacts">
      <h3 className="rail-heading">Artifacts</h3>
      <ul role="listbox" tabIndex={0} onKeyDown={onKey} aria-label="Artifacts">
        {listed.map((a) => {
          const info = artifacts[a.id];
          const exists = !!info?.exists;
          const badge = !exists ? null : !seen[a.id] ? "new" : info.updated_at && info.updated_at > seen[a.id]! ? "updated" : null;
          return (
            <li key={a.id} role="option" aria-selected={selected === a.id} aria-disabled={!exists} className={[exists ? "" : "unavailable", selected === a.id ? "selected" : ""].join(" ")} onClick={() => exists && onSelect(selected === a.id ? null : a.id)}>
              <span className="artifact-name"><DocIcon />{labels?.[a.id] ?? a.label}</span>
              {badge === "new" && <span className="dot new" aria-label="new" />}
              {badge === "updated" && <span className="chip">updated</span>}
            </li>
          );
        })}
      </ul>
      {children}
    </aside>
  );
}

/** Each artifact is a document: the rail says so at a glance. */
function DocIcon() {
  return <svg className="doc-icon" width="14" height="14" viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H6.5A1.5 1.5 0 0 0 5 4.5v15A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5V8z" /><path d="M14 3v5h5" /><path d="M8.5 13h7M8.5 16.5h5" /></svg>;
}
